import { useMutation, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { getCustomerQueryKey } from "modules/customers/lib/customerQuery"
import { useTranslation } from "react-i18next"
import {
  ConversationResponseDataSchema,
  ConversationTestingExperimentSchema,
  ConversationTestingResponseDataSchema,
  DataResponse,
} from "schema"
import { toast } from "sonner"
import { z } from "zod"
import { getConversationQueryKey } from "../lib/conversationQuery"
import { getConversationsQueryKey } from "../lib/conversationsQuery"

type Response = DataResponse<z.infer<typeof ConversationTestingResponseDataSchema>>

type Params = {
  customerId: string
  campaignId: string
  experiment?: z.infer<typeof ConversationTestingExperimentSchema> | null
}

type Props = {
  onSuccess?: (response: DataResponse<z.infer<typeof ConversationResponseDataSchema>>) => void
}

export function useStartTestingConversationMutation({ onSuccess }: Props) {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: async ({ campaignId, customerId, experiment }: Params) => {
      const response = await axios.post<Response>(`/conversations/testing/${customerId}`, {
        campaignId,
        experiment,
      })

      return response.data
    },
    onSuccess: (response) => {
      if ("filterOutReasons" in response.data && response.data.filterOutReasons) {
        const toastContent = (
          <div className="text-sm flex flex-col gap-0.5 w-full">
            <span className="text-lg font-semibold">
              {t("conversations.chat.cantCreate.title")}
            </span>
            <div className="flex flex-col">
              <span className="text-sm text-foreground font-normal">
                {t("conversations.chat.cantCreate.description")}
              </span>
              <ul>
                {response.data.filterOutReasons?.map((reason) => (
                  <li
                    key={reason}
                  >{`- ${t(`campaigns.detail.logs.filterOutReasons.${reason}`)}`}</li>
                ))}
              </ul>
            </div>
          </div>
        )

        toast(<div className="flex flex-col gap-2 w-full">{toastContent}</div>, {
          duration: 5000,
          description: t("conversations.chat.cantCreate.footer"),
        })

        return
      } else if ("id" in response.data) {
        queryClient.invalidateQueries({ queryKey: getConversationsQueryKey() })
        queryClient.invalidateQueries({
          queryKey: getConversationQueryKey(response.data.id),
        })
        queryClient.invalidateQueries({ queryKey: getCustomerQueryKey() })

        onSuccess?.(response as DataResponse<z.infer<typeof ConversationResponseDataSchema>>)
      }
    },
  })
}
